import { ApolloClient, useQuery, gql, InMemoryCache } from "@apollo/client";
import Settings from "./Settings";
import WeatherData from "./WeatherData";
import Level from "./Level";
import Rain from "./Rain";
import Temperature from "./Temperature";

// TypeScript interfaces for new GraphQL API structure
interface StationInfo {
    codigo: string;
    name: {
        prefix: string;
        general: string;
    }
    show: boolean;
    Type: string;
    TimeStamp?: string;
    // Weather data fields
    data: {
        rio: {
            rio_nivel: {
                value: number;
            }
        }
        temperatura: {
            atual: {
                value: number;
            }
        }
        senstermica: {
            atual: {
                value: number;
            }
        }
        umidade: {
            atual: {
                value: number;
            }
        }
        chuva: {
            acumulado: {
                min005: {
                    value: number;
                }
                h001: {
                    value: number;
                }
                h003: {
                    value: number;
                }
                h006: {
                    value: number;
                }
                h012: {
                    value: number;
                }
                h024: {
                    value: number;
                }
                h048: {
                    value: number;
                }
                h072: {
                    value: number;
                }
                h096: {
                    value: number;
                }
                h120: {
                    value: number;
                }
                h144: {
                    value: number;
                }
                h168: {
                    value: number;
                }
            }
        }
    }
    [key: string]: any; // Allow for additional fields
}

interface StationsResponse {
    tags_data: { 
        qualle_meteorologia: StationInfo
    };
}

// Interfaces for historical data (river level)
interface HistoricalDataPoint {
    rio_nivel?: number;
    temp_media?: number;
    sens_termica?: number;
    ts: string;
}

interface HistoricalDataResponse {
    historic: {
        items: HistoricalDataPoint[]
    }
}

interface WeatherStationData {
    nome: string;
    chuva_now?: number;
    chuva_001h?: number;
    chuva_003h?: number;
    chuva_006h?: number;
    chuva_012h?: number;
    chuva_024h?: number;
    chuva_048h?: number;
    chuva_072h?: number;
    chuva_096h?: number;
    chuva_120h?: number;
    chuva_144h?: number;
    chuva_168h?: number;
    umidade?: number;
    nivel_rio?: number;
    nivel_rio_historico?: Array<{ nivel: number; t_stamp: string }>;
    temperatura_historico?: Array<{ temperatura: number; t_stamp: string }>;
    sens_termica_historico?: Array<{ sens_termica: number; t_stamp: string }>;
    temp_atual?: number;
    temp_sens?: number;
    tipo?: string;
}



// New query to fetch all weather station names
const GET_STATIONS = gql`
    query Tags_data {
        tags_data(clients: ["secretaria-de-defesa-civil"]) {
            qualle_meteorologia {
                codigo
                type
                name {
                    prefix
                    general
                    local
                }
                show
                data {
                    senstermica {
                        atual {
                            value
                        }
                    }
                    temperatura {
                        atual {
                            value
                        }
                    }
                    umidade {
                        atual {
                            value
                        }
                    }
                    rio {
					    rio_nivel {
						    value
					    }
				    }
                    chuva {
					    acumulado {
						    min005 {
							    value
						    }
						    h001 {
							    value
						    }
						    h003 {
							    value
						    }
                            h006 {
							    value
						    }
                            h012 {
							    value
						    }
                            h024 {
							    value
						    }
                            h024 {
							    value
						    }
                            h048 {
							    value
						    }
                            h072 {
							    value
						    }
                            h096 {
							    value
						    }
                            h120 {
							    value
						    }
                            h144 {
							    value
						    }
                            h168 {
							    value
						    }
					    }
				    }
                }
            }
        }
    }
`;

// Query to fetch historical data for river level
const GET_HISTORICAL_DATA = gql`
    query Historic($stationCode: String!, $startDate: String!, $endDate: String!, $interval: QueryInterval) {
        historic(
            system: Qualle_Hidrometeorologia
            client: "secretaria-de-defesa-civil"
            stationCode: $stationCode
            startDate: $startDate
            endDate: $endDate
            interval: $interval
            opts: { ordenacao: ASC }
	    )
    }
`;


export default class DataProvider {

    formatDate(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }

    /**
     * Fetch historical data for a specific station (used for river level history)
     */
    async getStationHistoricalData(stationCode: string): Promise<HistoricalDataPoint[]> {
        const client = new ApolloClient({
            uri: 'https://monitoramento.defesacivil.sc.gov.br/graphql',
            cache: new InMemoryCache()
        });
        
        try {

            const { data, error } = await client.query<HistoricalDataResponse>({
                query: GET_HISTORICAL_DATA,
                variables: { 
                    stationCode,
                    endDate: this.formatDate(new Date()),
                    startDate: this.formatDate(new Date(Date.now() - 86400000)),
                    interval: 'HOUR_1' 
                },
                errorPolicy: "ignore"
            });
            
            if (error) {
                console.error(`GraphQL error fetching data for station ${stationCode}:`, error);
                return [];
            }
            
            if (!data?.historic) {
                console.warn(`No historical data found for station ${stationCode}`);
                return [];
            }
            
            return data.historic.items;
        } catch (error) {
            console.error(`Error fetching historical data for station ${stationCode}:`, error);
            return [];
        }
    }

    /**
     * Fetch all weather station names using the new GraphQL API format
     */
    async getStationNames(): Promise<Map<string, StationInfo>> {
        const client = new ApolloClient({
            uri: 'https://monitoramento.defesacivil.sc.gov.br/graphql',
            cache: new InMemoryCache()
        });
        
        try {
            const { data, error } = await client.query<StationsResponse>({
                query: GET_STATIONS,
                errorPolicy: "ignore"
            });
            
            if (error) {
                console.error('GraphQL error fetching stations:', error);
                throw new Error(`Failed to fetch station names: ${error.message}`);
            }
            
            if (!data?.tags_data) {
                throw new Error('Invalid response structure: missing tags data');
            }
            
            const stationsMap = new Map<string, StationInfo>();
            data.tags_data.qualle_meteorologia.forEach((stationInfo: StationInfo) => {
                if (stationInfo.show) {
                    stationsMap.set(stationInfo.codigo, stationInfo);
                }
            });
            
            return stationsMap;
        } catch (error) {
            console.error('Error fetching station names:', error);
            throw error;
        }
    }

    async getData(): Promise<WeatherData[]> {      
        // First, get all station names using the new API
        let stationsMap: Map<string, StationInfo>;
        try {
            stationsMap = await this.getStationNames();
        } catch (error) {
            console.error('Failed to fetch station names, falling back to old API:', error);
            return [];
        }
        
        // Extract weather data directly from station payload
        const mappedData = await this.mapStationsToWeatherData(stationsMap);
        
        return this.processWeatherData(mappedData);
    }

    private extractRainVolumes(data: any) : Rain[] {
        const rainVolumes : Rain[] = [];
        rainVolumes.push(new Rain(data.chuva_072h, 72));
        rainVolumes.push(new Rain(data.chuva_048h, 48));
        rainVolumes.push(new Rain(data.chuva_024h, 24));
        rainVolumes.push(new Rain(data.chuva_012h, 12));
        rainVolumes.push(new Rain(data.chuva_006h, 6));
        rainVolumes.push(new Rain(data.chuva_001h, 1));
        return rainVolumes;
    }

    /**
     * Build river level history from historical data points
     */
    private buildRiverLevelHistory(historicalData: HistoricalDataPoint[]): Array<{ nivel: number; t_stamp: string }> | undefined {
        if (!historicalData || historicalData.length === 0) {
            return undefined;
        }
        
        return historicalData
            .filter(point => point.rio_nivel !== undefined && point.rio_nivel !== null)
            .map(point => ({
                nivel: point.rio_nivel!,
                t_stamp: point.ts
            }));
    }

    private buildTemperatureHistory(historicalData: HistoricalDataPoint[]): Array<{ temperatura: number; t_stamp: string }> | undefined {
        if (!historicalData || historicalData.length === 0) {
            return;
        }
        
        return historicalData
            .filter(point => point.temp_media !== undefined && point.temp_media !== null && point.temp_media < 100)
            .map(point => ({
                temperatura: point.temp_media!,
                t_stamp: point.ts
            }));
    }

    private buildTemperatureFeelHistory(historicalData: HistoricalDataPoint[]): Array<{ sens_termica: number; t_stamp: string }> | undefined {
        if (!historicalData || historicalData.length === 0) {
            return;
        }
        
        return historicalData
            .filter(point => point.sens_termica !== undefined && point.sens_termica !== null && point.sens_termica > -100)
            .map(point => ({
                sens_termica: point.sens_termica!,
                t_stamp: point.ts
            }));
    }

    /**
     * Map station information to weather data structure using data directly from station payload
     */
    private async mapStationsToWeatherData(stationsMap: Map<string, StationInfo>): Promise<Map<string, WeatherStationData>> {
        const weatherDataMap = new Map<string, WeatherStationData>();
        
        // Get enabled cities from settings to optimize API calls
        const enabledCities = Settings.getSavedCities();
        
        // Process stations in parallel to fetch historical data
        const stationPromises = Array.from(stationsMap.entries()).map(async ([stationCode, stationInfo]) => {
            // Extract city name from station info
            const cityName = this.extractCityName(stationInfo);
            if (!cityName) {
                return null;
            }
            
            // Only fetch historical data if the city is enabled in settings
            let historicalData: HistoricalDataPoint[] = [];
            let riverLevelHistory: Array<{ nivel: number; t_stamp: string }> | undefined = undefined;
            let temperatureHistory: Array<{ temperatura: number; t_stamp: string }> | undefined = undefined;
            let temperatureFeelHistory: Array<{ sens_termica: number, t_stamp: string }> | undefined = undefined;
            
            if (enabledCities.includes(cityName)) {
                // Fetch historical data for river level
                historicalData = await this.getStationHistoricalData(stationCode);
                riverLevelHistory = stationInfo.type === 'Hidro' ? this.buildRiverLevelHistory(historicalData) : [];
                temperatureHistory = this.buildTemperatureHistory(historicalData);
                temperatureFeelHistory = this.buildTemperatureFeelHistory(historicalData);
            }
            
            // Create weather data structure with data directly from station payload
            const weatherData: WeatherStationData = {
                nome: cityName || stationInfo.name.general,
                tipo: stationInfo.type,
                // Extract rain data for different time periods from station payload
                chuva_now: stationInfo.data?.chuva?.acumulado.min005.value,
                chuva_001h: stationInfo.data?.chuva?.acumulado.h001.value,
                chuva_003h: stationInfo.data?.chuva?.acumulado.h003.value,
                chuva_006h: stationInfo.data?.chuva?.acumulado.h006.value,
                chuva_012h: stationInfo.data?.chuva?.acumulado.h012.value,
                chuva_024h: stationInfo.data?.chuva?.acumulado.h024.value,
                chuva_048h: stationInfo.data?.chuva?.acumulado.h048.value,
                chuva_072h: stationInfo.data?.chuva?.acumulado.h072.value,
                chuva_096h: stationInfo.data?.chuva?.acumulado.h096.value,
                chuva_120h: stationInfo.data?.chuva?.acumulado.h120.value,
                chuva_144h: stationInfo.data?.chuva?.acumulado.h144.value,
                chuva_168h: stationInfo.data?.chuva?.acumulado.h168.value,
                // Extract current weather conditions from station payload
                umidade: stationInfo.data?.umidade?.atual.value,
                temp_atual: stationInfo.data?.temperatura?.atual.value,
                temp_sens: stationInfo.data?.senstermica?.atual.value,
                // Extract current river level from station payload
                nivel_rio: stationInfo.data?.rio?.rio_nivel.value,
                // Add historical river level data
                nivel_rio_historico: riverLevelHistory,
                temperatura_historico: temperatureHistory,
                sens_termica_historico: temperatureFeelHistory
            };
            
            return { cityName, weatherData };
        });
        
        // Wait for all station data to be processed
        const results = await Promise.all(stationPromises);
        
        // Combine data from multiple stations for the same city
        results.forEach(result => {
            if (!result) return;
            
            const { cityName, weatherData } = result;
            
            if (weatherDataMap.has(cityName)) {                
                // Merge with existing data, keeping non-undefined values
                const existingData = weatherDataMap.get(cityName)!;
                const combinedData: WeatherStationData = {
                    nome: existingData.nome || weatherData.nome,
                    tipo: existingData.tipo || weatherData.tipo,
                    // Combine rain data - use existing if available, otherwise use new
                    chuva_now: existingData.chuva_now ?? weatherData.chuva_now,
                    chuva_001h: existingData.chuva_001h ?? weatherData.chuva_001h,
                    chuva_003h: existingData.chuva_003h ?? weatherData.chuva_003h,
                    chuva_006h: existingData.chuva_006h ?? weatherData.chuva_006h,
                    chuva_012h: existingData.chuva_012h ?? weatherData.chuva_012h,
                    chuva_024h: existingData.chuva_024h ?? weatherData.chuva_024h,
                    chuva_048h: existingData.chuva_048h ?? weatherData.chuva_048h,
                    chuva_072h: existingData.chuva_072h ?? weatherData.chuva_072h,
                    chuva_096h: existingData.chuva_096h ?? weatherData.chuva_096h,
                    chuva_120h: existingData.chuva_120h ?? weatherData.chuva_120h,
                    chuva_144h: existingData.chuva_144h ?? weatherData.chuva_144h,
                    chuva_168h: existingData.chuva_168h ?? weatherData.chuva_168h,
                    // Combine weather conditions
                    umidade: existingData.umidade ?? weatherData.umidade,
                    temp_atual: existingData.temp_atual ?? weatherData.temp_atual,
                    temp_sens: existingData.temp_sens ?? weatherData.temp_sens,
                    // Combine river level data
                    nivel_rio: existingData.nivel_rio ?? weatherData.nivel_rio,
                    // Combine river level history - use the one with data, or merge if both have data
                    nivel_rio_historico: existingData.nivel_rio_historico && existingData.nivel_rio_historico.length > 0 ? existingData.nivel_rio_historico : weatherData.nivel_rio_historico,
                    temperatura_historico: existingData && existingData.temperatura_historico && existingData.temperatura_historico.length > 0 ? existingData.temperatura_historico : weatherData.temperatura_historico,
                    sens_termica_historico: existingData && existingData.sens_termica_historico && existingData.sens_termica_historico.length > 0 ? existingData.sens_termica_historico : weatherData.sens_termica_historico
                };
                weatherDataMap.set(cityName, combinedData);
            } else {
                // First station for this city
                weatherDataMap.set(cityName, weatherData);
            }
        });
        
        return weatherDataMap;
    }

    /**
     * Extract city name from station information using regex patterns
     */
    private extractCityName(stationInfo: StationInfo): string | null {      
        // Fallback: try to extract from Name/General or Name/Local directly
        if (stationInfo.name.general) {
            let name = stationInfo.name.general;
            name = name.replace(/^(?:SDC-SC\b\s*)?(.*?)(?:\s*(?:-.*|\(\w\)|\d+))?\s*$/, "$1");
            return name.trim();
        }
        return null;
    }

    /**
     * Process weather data map into final WeatherData array
     */
    private async processWeatherData(mappedData: Map<string, WeatherStationData>): Promise<WeatherData[]> {
        localStorage.setItem('lastUpdate', Math.floor(new Date().getTime() / 1000).toString());

        const selectedCities = Settings.getSavedCities();
        let dataBlumenau: any = null;
        if (selectedCities.includes("Blumenau")) {
            try {
                const alertaBluResponse = await fetch('https://juw5k0e325.execute-api.us-east-1.amazonaws.com/default/alertablu-proxy-function');
                dataBlumenau = await alertaBluResponse.json();
            } catch {
                console.log('Failed to fetch data for Blumenau');
            }
        }

        return Array.from(mappedData.entries())
            .filter(([key, value]) => value.tipo !== 'Barragem')
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([key, value]) => {
                let riverLevel = value.nivel_rio;
                let levelHistory = value.nivel_rio_historico ? value.nivel_rio_historico.map((h: any) => new Level(h.nivel, new Date(h.t_stamp))) : [];
                if (key === 'Blumenau' && dataBlumenau && dataBlumenau.niveis) {
                    riverLevel = dataBlumenau.niveis[dataBlumenau.niveis.length - 1].nivel;
                    levelHistory = dataBlumenau.niveis.map((n: any) => new Level(n.nivel, new Date(n.horaLeitura)));
                }
                const rainVolumes = this.extractRainVolumes(value);
                const weatherData = new WeatherData(
                    key, 
                    value.chuva_now ?? 0, 
                    value.umidade ?? 0, 
                    riverLevel ?? 0, 
                    value.temp_atual ?? 0, 
                    value.temp_sens ?? 0,
                    true
                );
                weatherData.setRiverLevels(levelHistory);
                weatherData.setRainVolumes(rainVolumes);
                weatherData.setTemperatureHistory(value.temperatura_historico ? value.temperatura_historico.map((h: any) => new Temperature(h.temperatura, new Date(h.t_stamp))) : []);
                return weatherData;
            });
    }

}
